import {Component, Input, ViewEncapsulation, inject, ChangeDetectionStrategy} from '@angular/core';
import {DatePipe} from '@angular/common';
import {TranslateService} from '@ngx-translate/core';
import {PdfDetails} from '../../models/pdf/pdf-details';
import {Question} from '../../models/definition/question';
import {Category} from '../../models';
import {IQuestionType} from '../../models';
import {PdfService} from '../../services';

@Component({
    selector: 'app-pdf-page-project-analysis',
    templateUrl: './pdf-page-project-analysis.component.html',
    styleUrls: ['./pdf-page-project-analysis.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PdfPageProjectAnalysisComponent {
    private datePipe = inject(DatePipe);
    private translateService = inject(TranslateService);

    @Input() pdfDetails: PdfDetails;
    @Input() options: string[] = [];

    public get projectintakeOnly(): boolean {
        return this.options?.includes(PdfService.PROJECTINTAKE_ONLY) ?? false;
    }

    public get categories(): Category[] {
        const categories = this.pdfDetails.projectAnalysisDefinition?.categories ?? [];

        if (!this.projectintakeOnly) {
            return categories;
        }

        return categories
            .map((category) => ({
                ...category,
                questions: category.questions.filter((question) => !!question.isQuickScan)
            }) as Category)
            .filter((category) => category.questions.length > 0);
    }

    public getAnswer(question: Question): string {
        const answers = this.pdfDetails.projectDetails.projectAnalysisAnswers;

        if (question.type === IQuestionType.MULTIPLECHOICE) {
            const selectedIds = answers.getAnswerIdsForQuestion(question);
            return question.possibleAnswers
                .filter((possibleAnswer) => selectedIds.includes(possibleAnswer.id))
                .map((possibleAnswer) => `${this.colorScoreIndicator(possibleAnswer.colorScore)}${possibleAnswer.answer}`)
                .join(', ');
        }

        const entry = answers.getAnswerForQuestion(question);

        if (entry.answerId != null) {
            const selected = question.possibleAnswers.find((possibleAnswer) => possibleAnswer.id === entry.answerId);
            return selected ? `${this.colorScoreIndicator(selected.colorScore)}${selected.answer}` : '';
        }
        if (entry.answerAsText) {
            return entry.answerAsText.trim();
        }
        if (entry.answerAsDate) {
            return this.datePipe.transform(entry.answerAsDate, this.translateService.instant('DATE.short'));
        }
        if (entry.answerAsInteger != null) {
            return `${entry.answerAsInteger}`;
        }
        if (entry.answerAsPercentage != null) {
            return `${entry.answerAsPercentage}%`;
        }
        if (entry.answerAsBool != null) {
            return this.translateService.instant(
                entry.answerAsBool ? 'COMPONENT.QUESTION_INPUT.boolean.true' : 'COMPONENT.QUESTION_INPUT.boolean.false'
            );
        }

        return '';
    }

    /**
     * Determines if the question and its answer should be shown in the pdf output.
     *
     * <p>This is only relevant for questions that are dependent on the answer of another question.
     */
    public showQuestion(question: Question): boolean {
        if (question.dependentQuestion === null) {
            return true
        }

        const dependentQuestion = this.pdfDetails.findProjectAnalysisQuestion(question.dependentQuestion.id);
        if (dependentQuestion !== null) {
            const answers = this.pdfDetails.projectDetails.projectAnalysisAnswers;
            const dependentAnswer = answers.getAnswerForQuestion(dependentQuestion);

            if (dependentQuestion.type === IQuestionType.MULTIPLECHOICE) {
                return dependentAnswer?.answerId === question.dependentQuestion.answerId;
            }

            if (dependentQuestion.type === IQuestionType.BOOL) {
                return dependentAnswer?.answerAsBool === question.dependentQuestion.boolValue;
            }
        }

        return true;
    }

    public getExplanation(question: Question): string {
        return this.pdfDetails.projectDetails.projectAnalysisAnswers.getAnswerForQuestion(question).remarks ?? '';
    }

    public colorScoreIndicator(colorScore?: string): string {
        switch (colorScore) {
            case 'RED': return '\u{1F534} ';
            case 'ORANGE': return '\u{1F7E0} ';
            case 'GREEN': return '\u{1F7E2} ';
            default: return '';
        }
    }

    public trackCategory(index: number, category: Category): string {
        return `${category.id}`;
    }

    public trackQuestion(index: number, question: Question): string {
        return `${question.id}`;
    }
}
